import type {
  FieldMapping as Mapping,
  MappingResponse,
  SchemaSetSnapshot,
  SchemaTableSnapshot,
} from '../../services/api';
import { normalizeTableKey } from '../utils/normalizeTableKey';
import { normaliseDisplayLabel } from './qualifiedNames';
import { collectMappingSourceFieldNames, splitSourceField } from './transformAnalysis';
import type { SnapshotTables } from './types';

export const collectTablesFromSnapshot = (snapshot?: SchemaSetSnapshot | null): SnapshotTables | undefined => snapshot?.tables;

// Extract file_name from a source_alias_map entry (supports both string and provenance dict)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const extractFileName = (entry: any): string | undefined => {
  if (!entry) return undefined;
  if (typeof entry === 'string') return entry;
  if (typeof entry === 'object' && entry.file_name) return entry.file_name as string;
  return undefined;
};

// Helper to find the actual key in the snapshot that corresponds to a raw reference (from mapping)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const resolveSchemaTableKey = (rawName: string, snapshot?: SchemaSetSnapshot | null, sourceAliasMap?: Record<string, any>): string | undefined => {
  if (!snapshot?.tables) {
    return undefined;
  }

  // 0. Source Alias Map Match (Deterministic from Backend)
  if (sourceAliasMap) {
    // Try exact match
    const exactEntry = sourceAliasMap[rawName];
    if (exactEntry) {
      const mappedName = extractFileName(exactEntry);
      if (mappedName && snapshot.tables[mappedName]) {
        return mappedName;
      }
    }
    // Try case-insensitive alias match
    const lowerRaw = rawName.toLowerCase();
    const aliasKey = Object.keys(sourceAliasMap).find(k => k.toLowerCase() === lowerRaw);
    if (aliasKey) {
      const mappedName = extractFileName(sourceAliasMap[aliasKey]);
      if (mappedName && snapshot.tables[mappedName]) {
        return mappedName;
      }
    }
  }

  const tables = snapshot.tables;
  const keys = Object.keys(tables);

  // 1. Exact Match
  if (tables[rawName]) {
    return rawName;
  }

  // 2. Remove Extension (e.g. "file.csv" -> "file")
  const noExt = rawName.replace(/\.[^/.]+$/, "");
  if (noExt !== rawName && tables[noExt]) {
    return noExt;
  }

  // 3. Case Insensitive Match
  const lowerRaw = rawName.toLowerCase();
  const caseMatch = keys.find(k => k.toLowerCase() === lowerRaw);
  if (caseMatch) {
    return caseMatch;
  }

  // 4. Namespace Strip (e.g. "ctms.sites" -> "sites") & Case Insensitive
  if (rawName.includes('.')) {
    const parts = rawName.split('.');
    const baseName = parts[parts.length - 1];

    if (tables[baseName]) {
      return baseName;
    }

    const lowerBase = baseName.toLowerCase();
    const baseMatch = keys.find(k => k.toLowerCase() === lowerBase);
    if (baseMatch) {
      return baseMatch;
    }
  }

  // 5. Deep Scan: Check 'name' property of tables (slow fallback)
  const deepMatch = keys.find(k => {
    const t = tables[k];
    return t.name?.toLowerCase() === lowerRaw || t.display_name?.toLowerCase() === lowerRaw;
  });
  if (deepMatch) {
    return deepMatch;
  }

  // 6. Normalized key match (handles slug ↔ DuckDB name mismatches)
  const nk = normalizeTableKey(rawName);
  if (nk) {
    const normalizedMatch = keys.find(k => normalizeTableKey(k) === nk);
    if (normalizedMatch) {
      return normalizedMatch;
    }
  }

  return undefined;
};

/** DDL keywords that should never appear as column names. */
export const DDL_NON_COLUMN_KEYWORDS = new Set(['CONSTRAINT', 'PRIMARY', 'FOREIGN', 'UNIQUE', 'CHECK', 'INDEX']);
export const isConstraintArtifact = (name: string): boolean => DDL_NON_COLUMN_KEYWORDS.has(name.toUpperCase());

export const collectFieldsFromSnapshot = (tableName: string, snapshot?: SchemaSetSnapshot | null, sourceAliasMap?: Record<string, string>): string[] => {
  // Use the robust resolver to find the correct key
  const resolvedKey = resolveSchemaTableKey(tableName, snapshot, sourceAliasMap);

  if (!resolvedKey || !snapshot?.tables) {
    console.warn(`[Canvas] Could not resolve schema table for alias: "${tableName}"`);
    return [];
  }
  const table = snapshot.tables[resolvedKey];
  const cols = table?.columns;
  if (!cols) return [];
  // Handle both array format and object/dictionary format for columns
  // (legacy saved recipes may still have dict-format columns)
  const names: string[] = Array.isArray(cols)
    ? cols.map(c => c.name)
    : Object.keys(cols);
  return names.filter((n): n is string => !!n && !isConstraintArtifact(n));
};

// Helper to extract and normalize source fields against schema
export const getNormalizedSourceFields = (
  mapping: Mapping,
  snapshot?: SchemaSetSnapshot | null,
  sourceAliasMap?: Record<string, string>
): string[] => {
  const rawFields = collectMappingSourceFieldNames(mapping);
  const fields =
    rawFields.length > 0
      ? rawFields
      : splitSourceField(mapping.source_field).length > 0
        ? splitSourceField(mapping.source_field)
        : [normaliseDisplayLabel(mapping.source_field) || ''];
  if (!snapshot) return fields;

  const sourceAlias = (mapping.source_file ?? 'source').trim() || 'source';
  // Get all valid schema fields for this source
  const schemaFields = collectFieldsFromSnapshot(sourceAlias, snapshot, sourceAliasMap);
  if (!schemaFields.length) return fields;

  // Create a lookup map (lowercase -> schema case)
  const schemaMap = new Map<string, string>();
  schemaFields.forEach(f => schemaMap.set(f.toLowerCase(), f));

  // Normalize
  return fields.map(f => {
    const canonical = schemaMap.get(f.toLowerCase());
    return canonical || f; // Fallback to original if not found
  });
};

export const collectTargetDisplayNames = (response: MappingResponse): Map<string, string> => {
  const names = new Map<string, string>();
  const registerTables = (tables?: SnapshotTables) => {
    if (!tables) {
      return;
    }
    Object.entries(tables).forEach(([table, definition]) => {
      if (names.has(table)) {
        return;
      }
      const friendly = normaliseDisplayLabel(definition?.display_name) ?? normaliseDisplayLabel(definition?.name);
      if (friendly) {
        names.set(table, friendly);
      }
    });
  };

  registerTables(collectTablesFromSnapshot(response.transformation_plan?.target_schema_snapshot));

  const metadata = response.metadata;
  if (metadata) {
    registerTables(collectTablesFromSnapshot(metadata.target_schema_snapshot));

    const context = metadata.schema_context;
    if (context) {
      registerTables(collectTablesFromSnapshot(context.target_schema_snapshot));
      registerTables(collectTablesFromSnapshot(context.target_schema_snapshot));
    }
  }

  return names;
};

// Helper to normalize columns to array format (handles both object and array)
export const normalizeColumnsToArray = (
  columns: SchemaTableSnapshot['columns'] | undefined
): Array<{ name: string; data_type?: string; display_name?: string; [key: string]: unknown }> => {
  if (!columns) return [];
  if (Array.isArray(columns)) {
    return columns.filter(c => c.name && !isConstraintArtifact(c.name));
  }
  // Handle object format: { column_name: { type, nullable, ... } }
  return Object.entries(columns)
    .filter(([name]) => !isConstraintArtifact(name))
    .map(([name, raw]) => {
      const def = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
      return {
        name,
        data_type: (def.type ?? def.data_type) as string | undefined,
        display_name: def.display_name as string | undefined,
        ...def,
      };
    });
};

// Helper to normalize target field names against schema
export const getNormalizedTargetField = (
  mapping: Mapping,
  targetSnapshot?: SchemaSetSnapshot | null,
  targetAliasMap?: Record<string, string>
): string => {
  const rawField = (mapping.target_field ?? '').trim();
  if (!rawField || !targetSnapshot?.tables) return rawField;

  const tableName = (mapping.target_table ?? 'target').trim() || 'target';
  const tableKey = resolveSchemaTableKey(tableName, targetSnapshot, targetAliasMap);
  if (!tableKey) return rawField;

  const tableDef = targetSnapshot.tables[tableKey];
  if (!tableDef?.columns) return rawField;

   const lowerRaw = rawField.toLowerCase();
   const columnsArray = normalizeColumnsToArray(tableDef.columns);
   const match = columnsArray.find(c => c.name.toLowerCase() === lowerRaw);
   return match ? match.name : rawField;
};
